import React, { forwardRef } from 'react';
import {
    Button,
    ButtonProps,
    Typography,
    TypographyProps,
    withStyles,
    CircularProgress,
    Box
} from '@material-ui/core';
import clsx from 'clsx';
import useBtnStyles from '../style/buttonStyle';

const PaddingBorderButton = withStyles((theme) => ({
    root: {
        padding: theme.spacing(1),
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
        border: `solid 2px ${theme.palette.primary.main}`
    }
}))(Button);

interface MyButtonProps extends ButtonProps {
    children: React.ReactNode;
    classes?: AppClassNameProps;
    onClick?: () => void;
    theme?: AppTheme;
    textProp?: TypographyProps;
    wider?: boolean;
}

const useMainTheme = (theme?: AppTheme, classes?: AppClassNameProps) => {
    const btnClasses = useBtnStyles();
    return {
        root: clsx(
            btnClasses.fillButton,
            theme === 'light' ? btnClasses.lightFillButton : btnClasses.darkFillButton,
            classes?.root
        ),
        text: clsx(classes?.text)
    };
};

const useBorderTheme = (theme?: AppTheme, classes?: AppClassNameProps) => {
    const btnClasses = useBtnStyles();
    return {
        root: clsx(
            btnClasses.borderButton,
            theme === 'light' ? btnClasses.lightBorderButton : btnClasses.darkBorderButton,
            classes?.root
        ),
        text: clsx(
            theme === 'light' ? btnClasses.lightBorderText : btnClasses.darkBorderText,
            classes?.text
        )
    };
};

export const MainButton = forwardRef(
    ({ children, theme, textProp, classes, onClick, ...rest }: MyButtonProps, ref) => {
        const { root, text } = useMainTheme(theme, classes);
        return (
            <PaddingBorderButton
                ref={ref as any}
                onClick={onClick}
                classes={{ root }}
                variant="contained"
                color="primary"
                {...rest}>
                <Typography
                    variant="caption"
                    color="inherit"
                    classes={{ root: text }}
                    {...textProp}>
                    {children}
                </Typography>
            </PaddingBorderButton>
        );
    }
);

export const MainSubmitButton = forwardRef(
    (
        {
            loading,
            children,
            buttonType = 'main',
            ...rest
        }: MyButtonProps & { buttonType?: 'main' | 'border'; loading: boolean },
        ref
    ) => {
        const Button = buttonType === 'main' ? MainButton : MainBorderButton;
        return (
            <Button disabled={loading} {...rest} ref={ref}>
                <Box display="flex" alignItems="center" justifyContent="center">
                    <span style={{ visibility: loading ? 'hidden' : 'visible' }}>{children}</span>
                    {loading && <CircularProgress style={{ position: 'absolute' }} size="1rem" />}
                </Box>
            </Button>
        );
    }
);

export const MainBorderButton = forwardRef(
    ({ children, theme, classes, textProp, onClick, ...rest }: MyButtonProps, ref) => {
        const { root, text } = useBorderTheme(theme, classes);
        return (
            <PaddingBorderButton
                ref={ref as any}
                variant="outlined"
                onClick={onClick}
                classes={{ root }}
                {...rest}>
                <Typography color="inherit" classes={{ root: text }} {...textProp}>
                    {children}
                </Typography>
            </PaddingBorderButton>
        );
    }
);
export const MainUnderlineButton = forwardRef(
    ({ children, classes, onClick, textProp,  ...rest }: MyButtonProps, ref) => {
        const btnClasses = useBtnStyles();
        return (
            <Button
                ref={ref as any}
                onClick={onClick}
                className={clsx(btnClasses.underline, classes?.root)}
                {...rest}>
                <Typography className={classes?.text} color="inherit" {...textProp}>
                    {children}
                </Typography>
            </Button>
        );
    }
);
